import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  styled,
  useTheme,
  useMediaQuery,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
  DescriptionOutlined,
  PersonOutlined,
  LogoutOutlined,
  GroupOutlined,
  CameraAltOutlined,
  ShieldOutlined,
  AutoAwesomeOutlined,
  LocalPoliceOutlined,
  ChatOutlined,
  DarkModeOutlined,
  LightModeOutlined,
  AssignmentTurnedInOutlined,
  BusinessOutlined,
} from "@mui/icons-material";
import { navy, periwinkle } from "@karda/design-tokens";
import { useCoApprovalEnabled } from "@/features/co-review/hooks/useCoApprovalEnabled";
import {
  KARDA_ADMIN_ROLE,
  REPORTS_MANAGE_STATION_PERMISSION,
  canManageUsers,
  canEditEvidence,
  isStationAdmin,
} from "../../shared/auth/authorization";
import LogoutModal from "@/shared/ui/modals/LogoutModal";
import { useNavigate, useLocation } from "react-router-dom";
import { useJobStore } from "../../store/jobStore";
import { queryClient } from "../../shared/api/queryClient";
import { useAuth } from "@workos-inc/authkit-react";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { useColorMode } from "../../context/ColorModeContext";
import { useIncidentManagementEnabled } from "../../pages/IncidentManagement/featureFlag";
import {
  useAdminDashboardEnabled,
  useInternalAffairsEnabled,
} from "../../pages/StationAdmin/featureFlag";

const drawerWidth = 240;
const closedDrawerWidth = 72; // Slimmer collapsed state

const DrawerHeader = styled("div", {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open?: boolean }>(({ theme, open }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: open ? "flex-end" : "center",
}));

interface SideBarProps {
  open: boolean;
  handleSideBarClick: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const Sidebar: React.FC<SideBarProps> = ({
  open,
  handleSideBarClick,
  mobileOpen,
  onMobileClose,
}) => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const openTransitionDuration = theme.transitions.duration.enteringScreen;
  const closeTransitionDuration = theme.transitions.duration.leavingScreen;
  const { signOut, role } = useAuth();
  const permissions = usePermissions();

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [showExpandedContent, setShowExpandedContent] = useState(open);

  const navigate = useNavigate();
  const location = useLocation();
  const clearStore = useJobStore((state) => state.clearStore);

  const hasManageUsersPermission = canManageUsers(permissions);
  const canViewTranscripts = canEditEvidence(permissions);
  const hasKardaAdminRole = role === KARDA_ADMIN_ROLE;
  const hasStationAdminRole = isStationAdmin(role);
  const coApprovalEnabled = useCoApprovalEnabled();
  const incidentsEnabled = useIncidentManagementEnabled();
  const adminDashboardEnabled = useAdminDashboardEnabled();
  const internalAffairsEnabled = useInternalAffairsEnabled();
  const showCoReview =
    coApprovalEnabled &&
    permissions.includes(REPORTS_MANAGE_STATION_PERMISSION);
  // The Station Admin entry points at /station-admin, which is reachable when
  // EITHER the dashboard or the IA tab is enabled (RequireStationAdminArea).
  const showStationAdmin =
    hasStationAdminRole && (adminDashboardEnabled || internalAffairsEnabled);

  const isRouteActive = (route: string | null) => {
    if (!route) return false;
    if (route === "/") return location.pathname === "/";
    return (
      location.pathname === route || location.pathname.startsWith(route + "/")
    );
  };

  const handleNavigate = (route: string) => {
    if (location.pathname === route) return;
    navigate(route);
  };

  const handleOpenLogoutModal = () => setIsLogoutModalOpen(true);
  const handleCloseLogoutModal = () => setIsLogoutModalOpen(false);

  const handleLogout = () => {
    clearStore();
    // Defense in depth: the WorkOS signOut is a full-page redirect that
    // destroys the heap anyway, but the officer's profile/templates/roster
    // (staleTime Infinity / gcTime 30min) must not depend on that staying
    // true — clear the query cache the moment auth ends.
    queryClient.clear();
    return signOut({ returnTo: `${window.location.origin}/login` });
  };

  type SideBarMenuItem = {
    text: string;
    icon: React.ReactNode;
    route: string | null;
    handleClick: () => void;
  };

  const topMenuItems: SideBarMenuItem[] = [
    {
      text: "My Reports",
      icon: <DescriptionOutlined sx={{ fontSize: 20 }} />,
      route: "/",
      handleClick: () => handleNavigate("/"),
    },
    ...(showCoReview
      ? [
          {
            text: "Commanding Officer Review",
            icon: <AssignmentTurnedInOutlined sx={{ fontSize: 20 }} />,
            route: "/co-review",
            handleClick: () => handleNavigate("/co-review"),
          },
        ]
      : []),
    ...(canViewTranscripts
      ? [
          {
            text: "Transcripts",
            icon: <ChatOutlined sx={{ fontSize: 20 }} />,
            route: "/transcripts",
            handleClick: () => handleNavigate("/transcripts"),
          },
        ]
      : []),
  ];

  const bottomMenuItems = [
    {
      text: "Profile",
      icon: <PersonOutlined sx={{ fontSize: 20 }} />,
      route: "/profile",
      handleClick: () => handleNavigate("/profile"),
    },
    ...(showStationAdmin
      ? [
          {
            text: "Station Admin",
            icon: <BusinessOutlined sx={{ fontSize: 20 }} />,
            route: "/station-admin",
            handleClick: () => handleNavigate("/station-admin"),
          },
        ]
      : []),
    ...(hasKardaAdminRole
      ? [
          {
            text: "KARDA Admin",
            icon: <ShieldOutlined sx={{ fontSize: 20 }} />,
            route: "/karda-admin",
            handleClick: () => handleNavigate("/karda-admin"),
          },
        ]
      : []),
    ...(hasManageUsersPermission
      ? [
          {
            text: "Manage Users",
            icon: <GroupOutlined sx={{ fontSize: 20 }} />,
            route: "/manage-users",
            handleClick: () => handleNavigate("/manage-users"),
          },
        ]
      : []),
    ...(hasManageUsersPermission
      ? [
          {
            text: "Connect Bodycam",
            icon: <CameraAltOutlined sx={{ fontSize: 20 }} />,
            route: "/connect-bodycam",
            handleClick: () => handleNavigate("/connect-bodycam"),
          },
        ]
      : []),
    {
      text: "Logout",
      icon: <LogoutOutlined sx={{ fontSize: 20 }} />,
      route: null,
      handleClick: handleOpenLogoutModal,
    },
  ];

  const handleGenerateClick = () => handleNavigate("/video-selection");

  useEffect(() => {
    if (!isLgUp) {
      setShowExpandedContent(true);
      return;
    }

    if (open) {
      setShowExpandedContent(true);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowExpandedContent(false);
    }, closeTransitionDuration);

    return () => window.clearTimeout(timeoutId);
  }, [closeTransitionDuration, isLgUp, open]);

  // Whether the drawer content is expanded (labels visible)
  // On mobile, when the drawer is open, it should be expanded; when closed, it is hidden entirely.
  const isExpanded = isLgUp ? open : true;
  const isContentExpanded = isLgUp ? showExpandedContent : true;
  const { mode, toggleColorMode } = useColorMode();
  const darkMode = mode === "dark";

  // Drawer content without wrapping it in another Drawer
  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowX: "hidden",
      }}
    >
      <LogoutModal
        open={isLogoutModalOpen}
        onClose={handleCloseLogoutModal}
        onLogout={handleLogout}
      />

      <DrawerHeader
        open={isContentExpanded}
        sx={{
          overflow: "visible",
          justifyContent: isContentExpanded ? "space-between" : "center",
          pl: isContentExpanded ? 2.5 : 0, // Centered when collapsed
          pr: isContentExpanded ? 1 : 0,
        }}
      >
        {isContentExpanded && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            OVERVIEW
          </Typography>
        )}

        {isLgUp && (
          <IconButton
            onClick={(e) => {
              handleSideBarClick();
              e.currentTarget.blur();
            }}
          >
            {open ? (
              <KeyboardDoubleArrowLeftOutlined />
            ) : (
              <KeyboardDoubleArrowRightOutlined />
            )}
          </IconButton>
        )}
      </DrawerHeader>

      <List>
        {topMenuItems.map((item) => (
          <Tooltip
            key={item.text}
            title={!isContentExpanded ? item.text : ""}
            placement="right"
            arrow
          >
            <ListItemButton
              selected={isRouteActive(item.route)}
              sx={{
                minHeight: 40,
                justifyContent: isContentExpanded ? "initial" : "center",
                mx: isContentExpanded ? 1 : "auto",
                width: isContentExpanded ? "auto" : 40,
                px: isContentExpanded ? 1.5 : 0,
                mb: 0.5,
                borderRadius: 2,
                color: isRouteActive(item.route)
                  ? "primary.main"
                  : "text.primary",
                bgcolor: isRouteActive(item.route)
                  ? "action.selected"
                  : "transparent",
                "&:hover": {
                  bgcolor: isRouteActive(item.route)
                    ? "action.selected"
                    : "action.hover",
                  color: "text.primary",
                },
                "& .MuiListItemIcon-root": {
                  color: "inherit",
                },
              }}
              onClick={item.handleClick}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isContentExpanded ? 2 : 0,
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {isContentExpanded && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: isRouteActive(item.route) ? 600 : 500,
                    lineHeight: 1.2,
                  }}
                  sx={{
                    opacity: isContentExpanded ? 1 : 0,
                    // Allow long labels (e.g. "Commanding Officer Review") to
                    // wrap onto a second line instead of being clipped.
                    whiteSpace: "normal",
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>

      <Tooltip title="Generate a new report" arrow placement="right">
        <Button
          variant="contained"
          startIcon={<AutoAwesomeOutlined sx={{ fontSize: 18 }} />}
          onClick={handleGenerateClick}
          sx={{
            mx: isContentExpanded ? 1 : "auto", // Center button when collapsed
            my: 1,
            width: isContentExpanded ? `calc(100% - ${theme.spacing(2)})` : 40,
            maxWidth: isContentExpanded
              ? `calc(100% - ${theme.spacing(2)})`
              : 40,
            minWidth: 0,
            height: 40,
            boxSizing: "border-box",
            overflow: "hidden",
            textTransform: "none",
            fontSize: "0.9rem",
            fontWeight: 600,
            borderRadius: 2,
            color: theme.palette.gradient.onBrand,
            background: theme.palette.gradient.brand,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: "translateY(-1px)",
              background: theme.palette.gradient.brandHover,
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "& .MuiButton-startIcon": {
              mr: isExpanded ? 1 : 0,
              ml: 0,
              transition: theme.transitions.create("margin", {
                easing: isExpanded
                  ? theme.transitions.easing.easeOut
                  : theme.transitions.easing.sharp,
                duration: isExpanded
                  ? openTransitionDuration
                  : closeTransitionDuration,
              }),
            },
            ...(!isContentExpanded && {
              minWidth: 40,
              p: 0, // Remove all padding to center icon
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }),
          }}
        >
          <Box
            component="span"
            sx={{
              display: "block",
              overflow: "hidden",
              whiteSpace: "nowrap",
              opacity: isExpanded ? 1 : 0,
              maxWidth: isExpanded ? "9rem" : 0,
              transition: theme.transitions.create(["max-width", "opacity"], {
                easing: isExpanded
                  ? theme.transitions.easing.easeOut
                  : theme.transitions.easing.sharp,
                duration: isExpanded
                  ? openTransitionDuration
                  : closeTransitionDuration,
              }),
            }}
          >
            New Report
          </Box>
        </Button>
      </Tooltip>

      {incidentsEnabled && (
        <Tooltip title="Open the incident workspace" arrow placement="right">
          <Button
            variant="contained"
            startIcon={<LocalPoliceOutlined sx={{ fontSize: 20 }} />}
            onClick={() => handleNavigate("/incidents")}
            sx={{
              mx: isContentExpanded ? 1 : "auto",
              // The glow bleeds ~half its 14px radius upward, so the stock
              // 8px stack gap reads as touching — clear the glow instead.
              mt: 1.5,
              mb: 1,
              width: isContentExpanded
                ? `calc(100% - ${theme.spacing(2)})`
                : 44,
              maxWidth: isContentExpanded
                ? `calc(100% - ${theme.spacing(2)})`
                : 44,
              minWidth: 0,
              height: 46,
              boxSizing: "border-box",
              overflow: "hidden",
              position: "relative",
              textTransform: "none",
              fontSize: "0.95rem",
              fontWeight: 700,
              letterSpacing: 0.2,
              borderRadius: 2,
              // A miniature of the Investigator hero panel: dark navy in BOTH
              // modes with a periwinkle bloom and edge — deliberately not the
              // mode-flipping brand gradient New Report wears, so the two
              // buttons can't read as siblings.
              color: theme.palette.common.white,
              background: `radial-gradient(ellipse at 85% -30%, ${alpha(periwinkle.light, 0.35)} 0%, transparent 60%), linear-gradient(135deg, ${navy[800]} 0%, ${navy[950]} 100%)`,
              border: "1px solid",
              borderColor: alpha(periwinkle.main, 0.5),
              boxShadow: isRouteActive("/incidents")
                ? `0 0 0 2px ${alpha(periwinkle.main, 0.6)}, 0 0 18px ${alpha(periwinkle.main, 0.5)}`
                : `0 0 14px ${alpha(periwinkle.main, 0.45)}`,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
                background: `radial-gradient(ellipse at 85% -30%, ${alpha(periwinkle.light, 0.5)} 0%, transparent 65%), linear-gradient(135deg, ${navy[700]} 0%, ${navy[900]} 100%)`,
                borderColor: alpha(periwinkle.light, 0.7),
                boxShadow: `0 0 22px ${alpha(periwinkle.main, 0.6)}`,
              },
              "&:active": {
                transform: "translateY(0)",
              },
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
              // Sheen sweep: base has no transition, so on unhover it snaps
              // back off-canvas invisibly instead of sweeping backwards.
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                bottom: 0,
                left: "-60%",
                width: "50%",
                background: `linear-gradient(105deg, transparent, ${alpha(theme.palette.common.white, 0.3)}, transparent)`,
                transform: "skewX(-20deg)",
                pointerEvents: "none",
              },
              "&:hover::after": {
                left: "110%",
                transition: "left 0.5s ease",
              },
              "@media (prefers-reduced-motion: reduce)": {
                "&::after": { display: "none" },
                "&:hover": { transform: "none" },
              },
              "& .MuiButton-startIcon": {
                mr: isExpanded ? 1 : 0,
                ml: 0,
                transition: theme.transitions.create("margin", {
                  easing: isExpanded
                    ? theme.transitions.easing.easeOut
                    : theme.transitions.easing.sharp,
                  duration: isExpanded
                    ? openTransitionDuration
                    : closeTransitionDuration,
                }),
              },
              ...(!isContentExpanded && {
                minWidth: 44,
                p: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }),
            }}
          >
            <Box
              component="span"
              sx={{
                display: "block",
                overflow: "hidden",
                whiteSpace: "nowrap",
                opacity: isExpanded ? 1 : 0,
                maxWidth: isExpanded ? "9rem" : 0,
                transition: theme.transitions.create(["max-width", "opacity"], {
                  easing: isExpanded
                    ? theme.transitions.easing.easeOut
                    : theme.transitions.easing.sharp,
                  duration: isExpanded
                    ? openTransitionDuration
                    : closeTransitionDuration,
                }),
              }}
            >
              Incidents
            </Box>
          </Button>
        </Tooltip>
      )}

      <Box sx={{ flexGrow: 1 }} />

      <Divider />

      <List>
        <Tooltip
          title={
            !isContentExpanded ? (darkMode ? "Light Mode" : "Dark Mode") : ""
          }
          placement="right"
          arrow
        >
          <ListItemButton
            sx={{
              minHeight: 40,
              justifyContent: isContentExpanded ? "initial" : "center",
              mx: isContentExpanded ? 1 : "auto",
              width: isContentExpanded ? "auto" : 40,
              px: isContentExpanded ? 1.5 : 0,
              mb: 0.5,
              mt: 1,
              borderRadius: 2,
              color: "text.primary",
              "&:hover": {
                bgcolor: "action.hover",
                color: "text.primary",
              },
            }}
            onClick={toggleColorMode}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isContentExpanded ? 2 : 0,
                justifyContent: "center",
                color: "inherit",
              }}
            >
              {darkMode ? (
                <LightModeOutlined sx={{ fontSize: 20 }} />
              ) : (
                <DarkModeOutlined sx={{ fontSize: 20 }} />
              )}
            </ListItemIcon>
            {isContentExpanded && (
              <ListItemText
                primary={darkMode ? "Light Mode" : "Dark Mode"}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
                sx={{
                  opacity: isContentExpanded ? 1 : 0,
                  whiteSpace: "nowrap",
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </List>

      {isContentExpanded && (
        <Box sx={{ px: 2.5, py: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            ACCOUNT
          </Typography>
        </Box>
      )}

      <List>
        {bottomMenuItems.map((item) => (
          <Tooltip
            key={item.text}
            title={!isContentExpanded ? item.text : ""}
            placement="right"
            arrow
          >
            <ListItemButton
              selected={item.route !== null && isRouteActive(item.route)}
              sx={{
                minHeight: 40,
                justifyContent: isContentExpanded ? "initial" : "center",
                mx: isContentExpanded ? 1 : "auto",
                width: isContentExpanded ? "auto" : 40,
                px: isContentExpanded ? 1.5 : 0,
                mb: 0.5,
                borderRadius: 2,
                color:
                  item.route !== null && isRouteActive(item.route)
                    ? "primary.main"
                    : "text.primary",
                bgcolor:
                  item.route !== null && isRouteActive(item.route)
                    ? "action.selected"
                    : "transparent",
                "&:hover": {
                  bgcolor:
                    item.route !== null && isRouteActive(item.route)
                      ? "action.selected"
                      : "action.hover",
                  color: "text.primary",
                },
                "& .MuiListItemIcon-root": {
                  color: "inherit",
                },
              }}
              onClick={item.handleClick}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isContentExpanded ? 2 : 0,
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {isContentExpanded && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight:
                      item.route !== null && isRouteActive(item.route)
                        ? 600
                        : 500,
                  }}
                  sx={{
                    opacity: isContentExpanded ? 1 : 0,
                    whiteSpace: "nowrap",
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {/* Desktop: Permanent drawer */}
      {isLgUp && (
        <Drawer
          variant="permanent"
          sx={{
            "& .MuiDrawer-paper": {
              width: isExpanded ? drawerWidth : closedDrawerWidth,
              overflowX: "hidden",
              transition: theme.transitions.create("width", {
                easing: isExpanded
                  ? theme.transitions.easing.easeOut
                  : theme.transitions.easing.sharp,
                duration: isExpanded
                  ? openTransitionDuration
                  : closeTransitionDuration,
              }),
              borderRight: "2px solid",
              borderColor: "divider",
              marginTop: "64px", // AppBar height
              height: "calc(100% - 64px)",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile: Temporary drawer */}
      {!isLgUp && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "2px solid",
              borderColor: "divider",
              marginTop: "56px", // Mobile AppBar height
              height: "calc(100% - 56px)",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
